import { useState, useEffect, useCallback } from 'react';
import { securityProfilesAPI, adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit, Trash2, Users, Shield, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell } from '../components/AdminShared';

const PERMISSION_LABELS = {
  manage_invoices: "Manage Invoices",
  manage_fee_schedules: "Manage Fee Schedules",
  manage_pay_rates: "Manage Pay Rates",
  manage_orgs: "Manage Organizations",
  manage_interpreters: "Manage Interpreters",
  manage_languages: "Manage Languages",
  manage_language_requests: "Manage Language Requests",
  manage_clients: "Manage Clients",
  manage_credits: "Manage Credits",
  manage_sessions: "Manage Sessions",
  manage_policies: "Manage Policies",
  manage_forms: "Manage Forms",
  view_audit_log: "View Audit Log",
};

export default function AdminSecurityProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showMembers, setShowMembers] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [addUserId, setAddUserId] = useState('');

  const load = useCallback(async () => {
    try {
      const [pRes, permRes] = await Promise.all([securityProfilesAPI.list(), securityProfilesAPI.getPermissions()]);
      setProfiles(pRes.data);
      setAllPermissions(permRes.data);
    } catch { /* ignore - user may not have admin access */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      await securityProfilesAPI.create(form);
      toast.success('Profile created');
      setShowCreate(false);
      setForm({ name: '', description: '', permissions: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUpdate = async () => {
    try {
      await securityProfilesAPI.update(showEdit.id, form);
      toast.success('Profile updated');
      setShowEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this security profile?')) return;
    try {
      await securityProfilesAPI.delete(id);
      toast.success('Profile deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete built-in profile'); }
  };

  const openMembers = async (profile) => {
    setShowMembers(profile);
    try {
      const [mRes, uRes] = await Promise.all([securityProfilesAPI.getMembers(profile.id), adminAPI.getUsers()]);
      setMembers(mRes.data);
      setAllUsers(uRes.data || []);
    } catch { setMembers([]); setAllUsers([]); }
  };

  const addMember = async () => {
    if (!addUserId) return;
    try {
      await securityProfilesAPI.addMember(showMembers.id, addUserId);
      toast.success('Member added');
      setAddUserId('');
      const mRes = await securityProfilesAPI.getMembers(showMembers.id);
      setMembers(mRes.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeMember = async (userId) => {
    try {
      await securityProfilesAPI.removeMember(showMembers.id, userId);
      toast.success('Member removed');
      const mRes = await securityProfilesAPI.getMembers(showMembers.id);
      setMembers(mRes.data);
      load();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const togglePerm = (perm) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm) ? f.permissions.filter(p => p !== perm) : [...f.permissions, perm]
    }));
  };

  const PermGrid = () => (
    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
      {allPermissions.map(p => (
        <label key={p} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/50 rounded p-1.5">
          <Checkbox checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
          {PERMISSION_LABELS[p] || p}
        </label>
      ))}
    </div>
  );

  const nonMembers = allUsers.filter(u => !members.find(m => m.id === u.id));

  return (
    <AdminPageShell title="Security Profiles" subtitle="Manage profile groups and permissions" testId="admin-security-profiles-page"
      actions={<Button onClick={() => { setForm({ name: '', description: '', permissions: [] }); setShowCreate(true); }} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-profile-btn"><Plus className="w-4 h-4 mr-2" />New Profile</Button>}
      loading={loading}>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Profile</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead>Members</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {profiles.map(p => (
              <TableRow key={p.id} data-testid={`profile-row-${p.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[hsl(var(--brand))]" />
                    {p.name}
                    {p.builtin && <Badge variant="secondary" className="text-[10px]">Built-in</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{p.description}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(p.permissions || []).slice(0, 3).map(perm => <Badge key={perm} variant="outline" className="text-[10px]">{PERMISSION_LABELS[perm] || perm}</Badge>)}
                    {(p.permissions || []).length > 3 && <Badge variant="secondary" className="text-[10px]">+{p.permissions.length - 3}</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" className="gap-1" onClick={() => openMembers(p)} data-testid={`view-members-${p.id}`}>
                    <Users className="w-3 h-3" />{p.member_count || 0}
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setForm({ name: p.name, description: p.description, permissions: p.permissions || [] }); setShowEdit(p); }} data-testid={`edit-profile-${p.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                    {!p.builtin && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.id)} data-testid={`delete-profile-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {profiles.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No security profiles. Seed data or create one.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-profile-dialog">
          <DialogHeader><DialogTitle>Create Security Profile</DialogTitle><DialogDescription>Define a new permission group</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="profile-name-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="profile-desc-input" /></div>
            <div className="space-y-2"><Label>Permissions</Label><PermGrid /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} className="bg-[hsl(var(--brand))] text-white" data-testid="save-profile-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent data-testid="edit-profile-dialog">
          <DialogHeader><DialogTitle>Edit Security Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Permissions</Label><PermGrid /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEdit(null)}>Cancel</Button><Button onClick={handleUpdate} className="bg-[hsl(var(--brand))] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={!!showMembers} onOpenChange={() => setShowMembers(null)}>
        <DialogContent className="max-w-lg" data-testid="members-dialog">
          <DialogHeader><DialogTitle>{showMembers?.name} — Members</DialogTitle><DialogDescription>Manage who belongs to this profile group</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Select value={addUserId} onValueChange={setAddUserId}>
                <SelectTrigger className="flex-1" data-testid="add-member-select"><SelectValue placeholder="Select user to add..." /></SelectTrigger>
                <SelectContent>
                  {nonMembers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={addMember} disabled={!addUserId} data-testid="add-member-btn"><UserPlus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {members.map(m => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`member-${m.id}`}>
                  <div><p className="text-sm font-medium">{m.name || m.email}</p><p className="text-xs text-muted-foreground">{m.email} — {m.role}</p></div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeMember(m.id)}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              {members.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No members yet</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
